import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppBackButton from '../common/AppBackButton';
import ReferencePopup from '../Popups/ReferencePopup';
import { useSessionStore } from '../../store/sessionStore';
import { ReturnedReference } from '../Create-Portfolio/types';
import { getPortfolioReferences } from '../../utils/portfolioReferences';
import {
  getReferenceFeedbackSummary,
  parseReferenceVerificationAnswers,
} from '../../utils/referenceFeedback';
import {
  referenceResendError,
  resendReferenceVerificationEmail,
} from '../../utils/referenceResend';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import {
  Bell,
  Briefcase,
  Calendar,
  CheckCircle2,
  Circle,
  Clock,
  HelpCircle,
  List,
  Mail,
  Pencil,
  Plane,
  Send,
  Sparkles,
  ThumbsUp,
  Trash2,
  Users,
} from 'lucide-react';
import './Dashboard.css';
import './InnerPages.css';
import './ReferenceDetailPage.css';

type ReferenceNavState = { from?: string };

type RefRow = ReturnedReference & {
  verification_sent_at?: string | null;
  verified_at?: string | null;
};

function initialsFrom(name: string): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .map((p) => p.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

function parseIso(iso?: string | Date | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatRefDate(iso?: string | Date | null): string {
  const d = parseIso(iso);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatRefDateTime(iso?: string | Date | null): string {
  const d = parseIso(iso);
  if (!d) return '—';
  return `${d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })} at ${d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

function daysSince(iso?: string | Date | null): number {
  const d = parseIso(iso);
  if (!d) return 0;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

function daysAgoLabel(iso?: string | Date | null): string {
  const n = daysSince(iso);
  if (n === 0) return 'today';
  if (n === 1) return '1 day ago';
  return `${n} days ago`;
}

function openGlobalHelp() {
  document.querySelector<HTMLButtonElement>('.help-fab')?.click();
}

const ReferenceDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = (location.state as ReferenceNavState | null)?.from;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reference, setReference] = useState<RefRow | null>(null);
  const [resending, setResending] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [remindersSent, setRemindersSent] = useState(1);
  const portfolioFromStore = useSessionStore((s) => s.portfolio);

  const loadReference = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const portfolio =
        useSessionStore.getState().portfolio ??
        (await useSessionStore.getState().refreshPortfolio());
      const refs = getPortfolioReferences(portfolio) as RefRow[];
      const match = refs.find((r) => String(r.id) === String(id));
      setReference(match || null);
      if (!match) {
        setLoadError('We could not find this reference. It may have been removed.');
      } else {
        setRemindersSent(match.verification_sent_at ? 1 : 0);
      }
    } catch {
      setReference(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadReference();
  }, [loadReference]);

  useEffect(() => {
    if (!portfolioFromStore || !id) return;
    const refs = getPortfolioReferences(portfolioFromStore) as RefRow[];
    const match = refs.find((r) => String(r.id) === String(id));
    if (!match) return;
    setReference(match);
    setLoadError(null);
    setRemindersSent(match.verification_sent_at ? 1 : 0);
  }, [portfolioFromStore, id]);

  const answers = useMemo(
    () => parseReferenceVerificationAnswers(reference?.verificationAnswers),
    [reference]
  );

  const isVerified = Boolean(reference?.isReferenceConfirmed);
  const sentAt = reference?.verification_sent_at ?? reference?.created_at;
  const addedAt = reference?.created_at;
  const waitStart = sentAt ?? addedAt;

  const breadcrumbRoot = backTo === '/user-profile' ? 'Profile' : 'Dashboard';
  const breadcrumbRootPath = backTo === '/user-profile' ? '/user-profile' : '/dashboard';

  const handleResend = async () => {
    if (!reference?.email?.trim()) {
      toast.error('This reference does not have an email address. Add an email before resending.');
      return;
    }
    setResending(true);
    try {
      const detail = await resendReferenceVerificationEmail(reference.id);
      toast.success(detail);
      setRemindersSent((n) => Math.max(1, n + 1));
      await loadReference();
    } catch (err) {
      toast.error(referenceResendError(err));
    } finally {
      setResending(false);
    }
  };

  const confirmRemove = async () => {
    if (!reference) return;
    setRemoving(true);
    try {
      await axiosInstance.delete(`${API_ROUTES.REFERENCE.UPDATE_REFERENCE}/${reference.id}`);
      toast.success('Reference removed.');
      setShowRemoveModal(false);
      navigate('/dashboard/references', { replace: true });
    } catch {
      toast.error('Could not remove this reference. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return <PageBusyLoader pageName="Reference Check" />;
  }

  return (
    <div className="v1-dashboard-page ref-check-page">
      <main className="v1-dashboard-main ref-check-main">
        <header className="ref-check-top">
          <div className="ref-check-top-left">
            <div className="ref-check-nav-row">
              <AppBackButton to={backTo} />
              <nav className="ref-check-breadcrumbs" aria-label="Breadcrumb">
                <Link to={breadcrumbRootPath}>{breadcrumbRoot}</Link>
                <span aria-hidden>›</span>
                <Link to="/dashboard/references">References</Link>
                <span aria-hidden>›</span>
                <span className="ref-check-breadcrumbs-current">
                  {reference?.name || 'Reference'}
                </span>
              </nav>
            </div>
          </div>
          <div className="ref-check-top-help">
            <button type="button" className="ref-check-help-link" onClick={openGlobalHelp}>
              <HelpCircle size={16} aria-hidden />
              Need help?
            </button>
            <button type="button" className="ref-check-help-btn" onClick={openGlobalHelp}>
              Get Help
            </button>
          </div>
        </header>

        <h1 className="ref-check-title">Reference Check</h1>

        {loadError || !reference ? (
          <section className="v1-card ref-check-error-card">
            <p role="alert">{loadError || 'Reference not found.'}</p>
            <button
              type="button"
              className="v1-btn v1-btn-outline"
              onClick={() => navigate('/dashboard/references')}
            >
              See all references
            </button>
          </section>
        ) : (
          <div className="ref-check-layout">
            <div className="ref-check-main-col">
              <section className="ref-check-profile-card">
                <div className="ref-check-profile-left">
                  <div className="ref-check-avatar" aria-hidden>
                    {initialsFrom(reference.name || '')}
                  </div>
                  <div className="ref-check-profile-body">
                    <h2 className="ref-check-name">{reference.name}</h2>
                    {reference.role ? (
                      <p className="ref-check-meta-line">
                        <Briefcase size={15} aria-hidden />
                        {reference.role}
                      </p>
                    ) : null}
                    {reference.email ? (
                      <p className="ref-check-meta-line">
                        <Mail size={15} aria-hidden />
                        <a href={`mailto:${reference.email}`}>{reference.email}</a>
                      </p>
                    ) : null}
                    <p className="ref-check-meta-line ref-check-meta-muted">
                      <Calendar size={15} aria-hidden />
                      Reference added {daysAgoLabel(addedAt)} · Added by you
                    </p>
                  </div>
                </div>
                <div
                  className={`ref-check-status-box ${isVerified ? 'is-verified' : 'is-pending'}`}
                >
                  {isVerified ? (
                    <>
                      <CheckCircle2 size={22} aria-hidden />
                      <span className="ref-check-status-label">VERIFIED</span>
                      <p>This reference completed the verification form.</p>
                    </>
                  ) : (
                    <>
                      <Clock size={22} aria-hidden />
                      <span className="ref-check-status-label">AWAITING RESPONSE</span>
                      <p>The reference has not yet completed the verification form.</p>
                    </>
                  )}
                </div>
              </section>

              {!isVerified ? (
                <section className="ref-check-waiting-banner">
                  <div className="ref-check-waiting-icon" aria-hidden>
                    <Mail size={22} />
                  </div>
                  <div className="ref-check-waiting-text">
                    <h3>Waiting for Reference Response</h3>
                    <p>
                      We sent a verification request to{' '}
                      <strong>{reference.email || 'their email'}</strong>. They will receive an
                      email with a secure link to complete the reference form.
                    </p>
                  </div>
                  <div className="ref-check-waiting-aside">
                    <p>
                      <strong>Last email sent:</strong>
                      <br />
                      {formatRefDateTime(sentAt)}
                    </p>
                    <p>
                      <strong>Reminder sent:</strong>
                      <br />
                      {remindersSent === 0
                        ? 'Not yet'
                        : `${remindersSent} time${remindersSent === 1 ? '' : 's'}`}
                    </p>
                  </div>
                </section>
              ) : null}

              <div className="ref-check-actions">
                {!isVerified && reference.email ? (
                  <button
                    type="button"
                    className="v1-btn v1-btn-primary ref-check-action-primary"
                    disabled={resending}
                    onClick={() => void handleResend()}
                  >
                    <Send size={16} aria-hidden />
                    {resending ? 'Sending…' : 'Resend Email'}
                  </button>
                ) : null}
                {!isVerified ? (
                  <button
                    type="button"
                    className="v1-btn v1-btn-outline ref-check-action-outline"
                    onClick={() => setShowEditModal(true)}
                  >
                    <Pencil size={16} aria-hidden />
                    Edit Reference
                  </button>
                ) : null}
                <button
                  type="button"
                  className="v1-btn ref-check-action-danger"
                  disabled={removing}
                  onClick={() => setShowRemoveModal(true)}
                >
                  <Trash2 size={16} aria-hidden />
                  {removing ? 'Removing…' : 'Remove Reference'}
                </button>
              </div>

              {isVerified && answers ? (
                <div className="ref-check-feedback">
                  <div className="ref-check-meta-row">
                    <div className="ref-meta-pill ref-meta-pill--ok">
                      <Users size={14} aria-hidden />
                      <div>
                        <span className="ref-meta-label">Worked together</span>
                        <strong>{answers.worked_together ? 'Yes' : 'No'}</strong>
                      </div>
                    </div>
                    <div
                      className={`ref-meta-pill ${answers.recommend_hire ? 'ref-meta-pill--ok' : 'ref-meta-pill--neutral'}`}
                    >
                      <ThumbsUp size={14} aria-hidden />
                      <div>
                        <span className="ref-meta-label">Would recommend</span>
                        <strong>{answers.recommend_hire ? 'Yes' : 'No'}</strong>
                      </div>
                    </div>
                  </div>
                  <section className="ref-answer-card">
                    <div className="ref-answer-icon" aria-hidden>
                      <Users size={16} />
                    </div>
                    <div className="ref-answer-body">
                      <h3>How you worked together</h3>
                      <p>{answers.relationship_summary || '—'}</p>
                    </div>
                  </section>
                  <section className="ref-answer-card ref-answer-card--strengths">
                    <div className="ref-answer-icon" aria-hidden>
                      <Sparkles size={16} />
                    </div>
                    <div className="ref-answer-body">
                      <h3>Strengths they highlighted</h3>
                      <p>{answers.strengths || '—'}</p>
                    </div>
                  </section>
                  {answers.concerns ? (
                    <section className="ref-answer-card ref-answer-card--concerns">
                      <div className="ref-answer-body">
                        <h3>Concerns or context</h3>
                        <p>{answers.concerns}</p>
                      </div>
                    </section>
                  ) : null}
                  <p className="ref-summary-foot">
                    <strong>Recruiter summary:</strong>{' '}
                    {getReferenceFeedbackSummary(answers) || 'No summary available.'}
                  </p>
                </div>
              ) : null}

              <section className="ref-check-timeline-card">
                <div className="ref-check-card-head">
                  <List size={18} aria-hidden />
                  <h3>Activity Timeline</h3>
                </div>
                <ul className="ref-check-timeline">
                  <li className="is-done">
                    <span className="ref-check-timeline-icon done">
                      <CheckCircle2 size={16} />
                    </span>
                    <div>
                      <strong>Reference added</strong>
                      <span>{formatRefDateTime(addedAt)}</span>
                    </div>
                  </li>
                  <li className={sentAt ? 'is-done' : ''}>
                    <span className={`ref-check-timeline-icon ${sentAt ? 'done' : ''}`}>
                      {sentAt ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                    </span>
                    <div>
                      <strong>Verification email sent</strong>
                      <span>{sentAt ? formatRefDateTime(sentAt) : 'Not sent yet'}</span>
                    </div>
                  </li>
                  <li className={sentAt ? 'is-done' : ''}>
                    <span className={`ref-check-timeline-icon ${sentAt ? 'done' : ''}`}>
                      {sentAt ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                    </span>
                    <div>
                      <strong>Email delivered</strong>
                      <span>{sentAt ? formatRefDateTime(sentAt) : 'Pending delivery'}</span>
                    </div>
                  </li>
                  <li className={isVerified ? 'is-done' : 'is-current'}>
                    <span className={`ref-check-timeline-icon ${isVerified ? 'done' : 'current'}`}>
                      {isVerified ? <CheckCircle2 size={16} /> : <Clock size={16} />}
                    </span>
                    <div>
                      <strong>{isVerified ? 'Response received' : 'Awaiting response'}</strong>
                      <span>
                        {isVerified ? formatRefDateTime(reference.verified_at) : 'Current status'}
                      </span>
                    </div>
                  </li>
                </ul>
              </section>
            </div>

            <aside className="ref-check-sidebar">
              <div className="ref-check-stat-card">
                <Calendar size={18} className="ref-check-stat-ico green" />
                <span className="ref-check-stat-label">Days Waiting</span>
                <strong className="ref-check-stat-value">
                  {isVerified ? 0 : daysSince(waitStart)}
                </strong>
                <span className="ref-check-stat-sub">
                  {isVerified
                    ? `Completed ${formatRefDate(reference.verified_at)}`
                    : `Since ${formatRefDate(waitStart)}`}
                </span>
              </div>
              <div className="ref-check-stat-card">
                <Bell size={18} className="ref-check-stat-ico blue" />
                <span className="ref-check-stat-label">Reminders Sent</span>
                <strong className="ref-check-stat-value">{remindersSent}</strong>
                <span className="ref-check-stat-sub">
                  {sentAt ? `Last sent ${formatRefDate(sentAt)}` : 'No emails sent yet'}
                </span>
              </div>
              <div className="ref-check-stat-card">
                {isVerified ? (
                  <CheckCircle2 size={18} className="ref-check-stat-ico green" />
                ) : (
                  <Clock size={18} className="ref-check-stat-ico amber" />
                )}
                <span className="ref-check-stat-label">Status</span>
                <strong className="ref-check-stat-value">
                  {isVerified ? 'Complete' : 'Pending'}
                </strong>
                <span className="ref-check-stat-sub">
                  {isVerified ? 'Feedback received' : 'Awaiting response'}
                </span>
              </div>

              <section className="ref-check-completion-card">
                <div className="ref-check-card-head">
                  {isVerified ? (
                    <CheckCircle2 size={18} aria-hidden />
                  ) : (
                    <Clock size={18} aria-hidden />
                  )}
                  <h3>Reference Completion</h3>
                </div>
                <div className="ref-check-completion-body">
                  <div className="ref-check-completion-main">
                    <div className="ref-check-progress-head">
                      <span>Overall progress</span>
                      <strong>{isVerified ? 100 : 40}%</strong>
                    </div>
                    <div className="ref-check-progress-track">
                      <div
                        className={`ref-check-progress-fill ${isVerified ? 'is-full' : 'is-partial'}`}
                      />
                    </div>
                    <ul className="ref-check-steps">
                      <li className="is-done">
                        <CheckCircle2 size={16} aria-hidden />
                        <div>
                          <strong>Invitation Sent</strong>
                          <span>
                            {sentAt ? `Completed on ${formatRefDate(sentAt)}` : 'Pending'}
                          </span>
                        </div>
                      </li>
                      <li className={isVerified ? 'is-done' : 'is-active'}>
                        {isVerified ? (
                          <CheckCircle2 size={16} aria-hidden />
                        ) : (
                          <Clock size={16} aria-hidden />
                        )}
                        <div>
                          <strong>Awaiting Response</strong>
                          <span>{isVerified ? 'Completed' : 'In progress'}</span>
                        </div>
                      </li>
                      <li className={isVerified ? 'is-done' : ''}>
                        {isVerified ? (
                          <CheckCircle2 size={16} aria-hidden />
                        ) : (
                          <Circle size={16} aria-hidden />
                        )}
                        <div>
                          <strong>Verification Pending</strong>
                          <span>{isVerified ? 'Completed' : 'Not started'}</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <div
                    className={`ref-check-completion-art ${isVerified ? 'is-complete' : ''}`}
                    aria-hidden
                  >
                    {isVerified ? (
                      <>
                        <div className="ref-check-complete-badge">
                          <CheckCircle2 size={32} />
                        </div>
                        <p>All steps complete. Feedback is on your profile.</p>
                      </>
                    ) : (
                      <>
                        <div className="ref-check-envelope">
                          <Mail size={28} />
                        </div>
                        <Plane size={20} className="ref-check-plane" />
                        <p>We&apos;ll notify you once the reference completes the form.</p>
                      </>
                    )}
                  </div>
                </div>
              </section>
            </aside>
          </div>
        )}

        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          centered
          className="ss-app-modal ref-check-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit reference</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {reference?.email ? (
              <p className="ref-check-modal-sub">
                {reference.name}
                <span aria-hidden> · </span>
                {reference.email}
              </p>
            ) : null}
            <ReferencePopup
              embedded
              hideEmbeddedMeta
              initialReference={{
                id: reference?.id,
                name: reference?.name ?? '',
                email: reference?.email ?? '',
                role: reference?.role ?? '',
                organization: reference?.organization ?? '',
                phone: reference?.phone,
              }}
              onClose={() => {
                setShowEditModal(false);
                void (async () => {
                  await useSessionStore.getState().refreshPortfolio({
                    force: true,
                  });
                  await loadReference();
                })();
              }}
            />
          </Modal.Body>
        </Modal>

        <Modal
          show={showRemoveModal}
          onHide={() => !removing && setShowRemoveModal(false)}
          centered
          className="ss-app-modal ref-check-modal"
        >
          <Modal.Header closeButton={!removing}>
            <Modal.Title>Remove reference</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="ref-check-remove-copy">
              Remove <strong>{reference?.name || 'this reference'}</strong> from your references?
              This cannot be undone.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowRemoveModal(false)}
              disabled={removing}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void confirmRemove()} disabled={removing}>
              {removing ? 'Removing…' : 'Remove reference'}
            </Button>
          </Modal.Footer>
        </Modal>
      </main>
    </div>
  );
};

export default ReferenceDetailPage;
